/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { ServiceTypes } from 'constants/ServiceTypes';
import { DataTable } from '@trackman/react-ui';
import { observer } from 'mobx-react-lite';
import { ISession } from 'interfaces/session/ISession';
import { History } from 'history';
import { LoadingRenderer } from 'pages/SearchResults/LoadingComponent/LoadingRenderer';
import { SessionVerificationApiService } from 'services/data-services/SessionVerificationService';
import mappingHelpers from 'helpers/mappingHelpers';
import { MergeModal } from 'components/modals/MergeModal/MergeModal';
import { LogService } from 'services/LogService';
import { notify } from '../../services/NotificationService';
import {
  PageTitle,
  PageSubtitle,
  MergeButton,
  SearchButton,
  VerifiedCheckbox,
  CancelButton,
  TableDividerText,
  MasterGameHeader,
  HeaderTitle,
  HeaderContent,
  HeaderDivider,
  MergePage,
} from './styles/MergeStyles';

export interface IProps {
  location: Location;
  match: any;
  history: History;
}
export const Merge: React.FC<IProps> = observer(({ match, history }) => {
  const sessionVerificationStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const sessionService = useInjection<SessionVerificationApiService>(ServiceTypes.SessionVerificationApiService);
  const [relevantSessions, setRelevantSessions] = useState<ISession[]>([]);
  const [masterSession, setMasterSession] = useState<ISession>();
  const [slaveSession, setSlaveSession] = useState<ISession>();
  const { sessionId } = match.params;
  const [nextSuggestionTimeFrame, setNextSuggestionTimeFrame] = useState(3);
  const [isFetchGamesLoading, setIsFetchGamesLoading] = useState(false);
  const [isMergeInProgress, setIsMergeInProgress] = useState(false);
  const logService = useInjection<LogService>(ServiceTypes.LogService);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    sessionVerificationStore.isLoading = true;
    const masterSessionTask = sessionService.getSessionById(sessionId);
    const fetchSuggestionsTask = fetchGames(1);
    const [masterSession] = await Promise.all([masterSessionTask, fetchSuggestionsTask]);
    setMasterSession(masterSession);
    sessionVerificationStore.isLoading = false;
  };

  const fetchGames = async (numDays: number) => {
    setIsFetchGamesLoading(true);
    const suggestedSessions = await sessionService.getSuggestedSessions(sessionId, numDays);
    setRelevantSessions(suggestedSessions);
    setIsFetchGamesLoading(false);
  };

  const renderVerified = (isVerified: boolean) => {
    return isVerified ? <VerifiedCheckbox isChecked isDisabled label='' toggleCheck={() => {}} /> : null;
  };

  const merge = async () => {
    setIsMergeInProgress(true);
    try {
      await sessionService.mergeSessions(masterSession!.id, slaveSession!.id);
      notify('Success', 'Merge request has been created. It can take up to 15 min to be completed', 'success');
      history.replace('/');
    } catch (error) {
      setIsMergeInProgress(false);
      notify('Error', 'There was an error', 'error');
      logService.logError({
        error,
        message: `Exception when merging masterSession : ${masterSession!.id} , slaveSession : ${slaveSession!.id}`,
      });
    }
  };

  const renderMergeButton = (sessionId: string) => {
    return (
      <MergeButton
        outlook='solid'
        intent='primary'
        label='Merge'
        onClick={() => {
          setSlaveSession(relevantSessions.find((s) => s.id === sessionId));
        }}
        isDisabled={isMergeInProgress}
      />
    );
  };

  return (
    <div className='page'>
      <MergeModal
        visible={!!masterSession && !!slaveSession}
        masterSession={masterSession}
        isMergeInProgress={isMergeInProgress}
        slaveSession={slaveSession}
        history={history}
        onMergeCallback={merge}
        onDismissCallback={() => setSlaveSession(undefined)}
      />
      <MergePage>
        <PageTitle>Merging games</PageTitle>
        <CancelButton
          outlook='solid'
          intent='secondary'
          label='Cancel merge'
          isDisabled={sessionVerificationStore.isLoading}
          onClick={() => history.replace('/')}
        />
        <MasterGameHeader>
          <HeaderTitle>Master game</HeaderTitle>
          <HeaderContent>Plays from this game with be prioritized if there are any discrepencies between games.</HeaderContent>
          <HeaderDivider />
          <DataTable
            data={masterSession ? [masterSession] : []}
            className='master-game-table'
            loading={sessionVerificationStore.isLoading}
            columns={[
              { accessor: 'localStartTime', width: 150, Cell: (cellInfo, column) => mappingHelpers.timeDisplay(cellInfo.value) },
              { accessor: 'homeTeam.name' },
              { accessor: 'awayTeam.name' },
              { accessor: 'gameReference' },
              { accessor: 'isVerified', Cell: (cellInfo, column) => renderVerified(cellInfo.value), width: 80 },
              { accessor: 'id', width: 100, Cell: (cellInfo, column) => <></> },
            ]}
            pageSize={1}
            minRows={1}
            TheadComponent={(props) => null}
            LoadingComponent={() => LoadingRenderer(sessionVerificationStore.isLoading)}
          />
        </MasterGameHeader>

        <TableDividerText>Please select game you want to merge with</TableDividerText>
        <PageSubtitle>Suggested games</PageSubtitle>
        <DataTable
          data={relevantSessions}
          className='suggested-games-table'
          loading={isFetchGamesLoading}
          columns={[
            {
              Header: 'Time (Local)',
              accessor: 'localStartTime',
              width: 150,
              Cell: (cellInfo, column) => mappingHelpers.timeDisplay(cellInfo.value),
              sortable: true,
            },
            { Header: 'Home Team', accessor: 'homeTeam.name' },
            { Header: 'Away Team', accessor: 'awayTeam.name' },
            { Header: 'Game Ref.', accessor: 'gameReference', sortable: true },
            {
              Header: 'Verified',
              accessor: 'isVerified',
              Cell: (cellInfo, column) => renderVerified(cellInfo.value),
              width: 80,
              sortable: true,
            },
            { Header: '', accessor: 'id', width: 100, Cell: (cellInfo, column) => renderMergeButton(cellInfo.value) },
          ]}
          defaultPageSize={10}
          defaultSorted={[{ id: 'localStartTime', desc: false }]}
          showPagination
          LoadingComponent={() => LoadingRenderer(sessionVerificationStore.isLoading)}
        />
        <SearchButton
          outlook='solid'
          label={`Show games for next ${nextSuggestionTimeFrame} days`}
          isDisabled={isFetchGamesLoading}
          onClick={() => {
            fetchGames(nextSuggestionTimeFrame);
            setNextSuggestionTimeFrame(nextSuggestionTimeFrame + 3);
          }}
        />
      </MergePage>
    </div>
  );
});
